import { Button } from '@shared/ui/button/button.component';
import { EmptyState } from '@shared/ui/empty-state/empty-state.component';
import { Logo } from '@shared/ui/logo/logo.component';
import { useTranslation } from 'react-i18next';

import styles from './error-page.component.module.css';

interface ErrorPageProps {
	/** In dev, the caught Error whose message + stack we surface for debugging. */
	error?: Error;
}

/**
 * The branded page for an unexpected (non-404) error. Renders from the route
 * ErrorBoundary outside the app shell, so — like the 404 — it carries its own
 * wordmark and a way back to the board rather than a bare error string. In dev
 * the caught Error's message and stack are shown for debugging.
 */
export function ErrorPage({ error }: ErrorPageProps) {
	const { t } = useTranslation();

	return (
		<main className={styles.errorPage}>
			<a href="/" className={styles.logo} aria-label={t('nav.home', 'Home')}>
				<Logo />
			</a>
			<EmptyState
				icon={<span className={styles.badge}>{t('error.badge', 'Error')}</span>}
				title={t('error.title', 'Something went wrong')}
				description={t(
					'error.description',
					'An unexpected error broke this page. Head back to your board and pick up where you left off.',
				)}
			>
				<Button asChild>
					<a href="/">{t('error.home', 'Back to the board')}</a>
				</Button>
			</EmptyState>
			{error ? (
				<pre className={styles.stack}>
					<code>
						{error.message}
						{error.stack ? `\n\n${error.stack}` : null}
					</code>
				</pre>
			) : null}
		</main>
	);
}
